import { useRef, useState } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  Paperclip,
  FileText,
  Image as ImageIcon,
  X,
  Eye,
  Loader2,
  Upload,
  AlertCircle,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const MAX_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_COUNT = 10;
const ALLOWED_EXTS = ["jpg", "jpeg", "png", "heic", "heif", "pdf"];

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function fileExt(name) {
  if (!name || !name.includes(".")) return "";
  return name.split(".").pop().toLowerCase();
}

function isImage(att) {
  const mt = (att.mime_type || "").toLowerCase();
  if (mt.startsWith("image/")) return true;
  const ext = fileExt(att.filename);
  return ["jpg", "jpeg", "png", "heic", "heif"].includes(ext);
}

/**
 * Componentă reutilizabilă pentru gestionarea fișierelor atașate.
 *
 * Props:
 * - title: string — titlul secțiunii
 * - description: string (opțional)
 * - attachments: array — lista curentă (source pot fi patient/admin)
 * - onUpload: async (file, onProgress) => attachmentObj — logica de upload
 * - onDelete: async (fileId) => void — logica de ștergere
 * - onView: async (attachment) => void  (opțional) — preview/download
 * - onAiAnalyze: async (attachment) => void  (opțional) — analiză AI (arată buton ✨)
 * - aiLoadingId: string  (opțional) — id-ul fișierului pentru care rulează AI-ul (pentru spinner)
 * - onAttachmentAdded: (att) => void — sync UI după upload
 * - onAttachmentRemoved: (id) => void — sync UI după delete
 * - canUpload: bool — arată butonul de upload
 * - canDelete: bool — arată X pe fiecare item
 * - emptyText: string — afișat când nu există fișiere
 * - testIdPrefix: string — prefix pentru data-testid
 */
export const AttachmentsSection = ({
  title = "Atașează fișiere sau poze",
  description = "Acceptate: JPG, PNG, HEIC, PDF. Maxim 10 fișiere × 10 MB.",
  attachments = [],
  onUpload,
  onDelete,
  onView,
  onAiAnalyze,
  aiLoadingId = null,
  onAttachmentAdded,
  onAttachmentRemoved,
  canUpload = true,
  canDelete = true,
  emptyText = "Niciun fișier atașat.",
  testIdPrefix = "attachments",
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({}); // {filename: pct}

  const openPicker = () => {
    if (!inputRef.current) return;
    // reset ca să permitem re-selecția aceluiași fișier
    inputRef.current.value = "";
    inputRef.current.click();
  };

  const validateFile = (file) => {
    if (file.size > MAX_SIZE) {
      return `Fișierul „${file.name}” depășește 10 MB.`;
    }
    const ext = fileExt(file.name);
    const mt = (file.type || "").toLowerCase();
    const mtOk = mt.startsWith("image/") || mt === "application/pdf";
    const extOk = ALLOWED_EXTS.includes(ext);
    if (!mtOk && !extOk) {
      return `„${file.name}” nu este un format acceptat (doar JPG, PNG, HEIC, PDF).`;
    }
    return null;
  };

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    const fileArr = Array.from(files);
    const remaining = MAX_COUNT - attachments.length;
    if (remaining <= 0) {
      toast.error(`Ai atins limita de ${MAX_COUNT} fișiere.`);
      return;
    }
    const toUpload = fileArr.slice(0, remaining);
    if (fileArr.length > remaining) {
      toast.warning(`Doar primele ${remaining} fișiere vor fi încărcate (limita ${MAX_COUNT}).`);
    }

    setUploading(true);
    let successCount = 0;
    for (const file of toUpload) {
      const err = validateFile(file);
      if (err) {
        toast.error(err);
        continue;
      }
      try {
        const att = await onUpload(file, (pct) => {
          setProgress((p) => ({ ...p, [file.name]: pct }));
        });
        if (att && onAttachmentAdded) onAttachmentAdded(att);
        successCount += 1;
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "Eroare la încărcare.";
        toast.error(`„${file.name}”: ${msg}`);
      } finally {
        setProgress((p) => {
          const { [file.name]: _drop, ...rest } = p;
          return rest;
        });
      }
    }
    setUploading(false);
    if (successCount > 0) {
      toast.success(`${successCount} fișier(e) încărcat(e) cu succes.`);
    }
  };

  const handleDelete = async (att) => {
    if (!onDelete) return;
    if (!window.confirm(`Șterge fișierul „${att.filename}”?`)) return;
    try {
      await onDelete(att.id);
      if (onAttachmentRemoved) onAttachmentRemoved(att.id);
      toast.success("Fișier șters.");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Eroare la ștergere.";
      toast.error(msg);
    }
  };

  const handleView = async (att) => {
    if (!onView) return;
    try {
      await onView(att);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Nu am putut deschide fișierul.";
      toast.error(msg);
    }
  };

  const nearLimit = attachments.length >= MAX_COUNT;

  return (
    <Card data-testid={`${testIdPrefix}-section`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: "'Work Sans', sans-serif" }}>
          <Paperclip className="w-4 h-4" />
          {title}
        </CardTitle>
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {canUpload && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/heic,image/heif,application/pdf,.jpg,.jpeg,.png,.heic,.heif,.pdf"
              multiple
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
              data-testid={`${testIdPrefix}-file-input`}
            />
            <Button
              type="button"
              variant="outline"
              onClick={openPicker}
              disabled={uploading || nearLimit}
              className="gap-2"
              data-testid={`${testIdPrefix}-upload-btn`}
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              Atașează fișiere sau poze
            </Button>
            {nearLimit && (
              <p className="text-xs text-amber-700 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Ai atins limita maximă de {MAX_COUNT} fișiere.
              </p>
            )}
            {Object.entries(progress).map(([name, pct]) => (
              <div key={name} className="text-xs text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" />
                Se încarcă „{name}”… {pct}%
              </div>
            ))}
          </>
        )}

        {attachments.length === 0 ? (
          <p className="text-sm text-muted-foreground italic" data-testid={`${testIdPrefix}-empty`}>
            {emptyText}
          </p>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`${testIdPrefix}-list`}>
            {attachments.map((att) => (
              <li
                key={att.id}
                className="flex items-center gap-2 p-2 border rounded-md bg-slate-50 hover:bg-slate-100 transition"
                data-testid={`${testIdPrefix}-item-${att.id}`}
              >
                <div className="w-9 h-9 rounded bg-white border flex items-center justify-center flex-shrink-0">
                  {isImage(att) ? (
                    <ImageIcon className="w-4 h-4 text-primary" />
                  ) : (
                    <FileText className="w-4 h-4 text-primary" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate" title={att.filename}>
                    {att.filename}
                  </p>
                  <p className="text-xs text-muted-foreground">{formatSize(att.size)}</p>
                </div>
                <div className="flex items-center gap-1">
                  {onAiAnalyze && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-violet-600 hover:bg-violet-50"
                      onClick={() => onAiAnalyze(att)}
                      disabled={aiLoadingId === att.id}
                      title="Analizează cu AI"
                      data-testid={`${testIdPrefix}-ai-${att.id}`}
                    >
                      {aiLoadingId === att.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                  {onView && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleView(att)}
                      title="Vezi fișierul"
                      data-testid={`${testIdPrefix}-view-${att.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:bg-destructive/10"
                      onClick={() => handleDelete(att)}
                      title="Șterge fișierul"
                      data-testid={`${testIdPrefix}-delete-${att.id}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
